import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  DEFAULT_SCHEME,
  PALETTE_SCHEMES,
  buildPalette,
  isCustomPalette,
  paletteIds,
  paletteOf,
} from "../../../orion/config/dashboards";
import { ESTATE_PALETTE, customId, forgetCustomPalette, useCustomPalettes } from "./cardPalette";
import { greyscale, paletteFromImage } from "./paletteFromImage";

/* the control carries its own styles: five pages use it, and one of them
   (the dashboard) loads none of the card pages' stylesheets */
import "./CardPalettePicker.css";

/**
 * One colour picker, for everything that has colour.
 * ---------------------------------------------------------------------------
 * The dashboards, the wiki, discovery and the applications all draw from the
 * same fifteen palettes and the same ten swatches, so they share this - and,
 * through cardPalette, the same list of palettes someone has made. A custom
 * palette mixed on a board is on the cards' menu a moment later, and the other
 * way round.
 *
 * The trigger is the mark the documentation page uses for the same job (its
 * "Diagram color style" button): a palette, and nothing else. What is chosen
 * shows as the ring of colour around it rather than as a word - a name in a
 * toolbar is a word to read, and the colours say it faster.
 * ---------------------------------------------------------------------------
 */

/* The documentation page's own palette mark (Merchify's IconPalette). */
function IconPalette() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3a9 9 0 1 0 0 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.4-.3-.4-.5-.8-.5-1.3 0-1.1.9-2 2-2h2.3A4.2 4.2 0 0 0 21 12c0-5-4-9-9-9z" />
      <circle cx="7.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="9.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="14.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="16.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* A palette as a band of its own colours - five across, which is enough to
   tell two palettes apart without becoming a chart. */
function Swatches({ colors, count = 5 }) {
  const step = Math.max(1, Math.floor(colors.length / count));
  return (
    <span className="card-palette__swatches" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => colors[(i * step) % colors.length]).map((c, i) => (
        <i key={`${c}-${i}`} style={{ background: c }} />
      ))}
    </span>
  );
}

/* The estate's own tints are variables rather than values, so they show as
   the variables they are. */
const ESTATE_STRIP = [
  "var(--cp-estate-1)", "var(--cp-estate-2)", "var(--cp-estate-3)",
  "var(--cp-estate-4)", "var(--cp-estate-5)",
];

export default function CardPalettePicker({
  value,
  onChange,
  onApplyAll,
  scopeLabel = "this page",
  /* the estate's own tints are a card thing; a dashboard has no such ladder */
  allowEstate = true,
}) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  /* Where the menu is drawn. Normally the body, because every toolbar it hangs
     off is inside a plate with `overflow: hidden`. Inside a dialog, the body is
     the wrong side of it: the dialog paints over anything outside itself and
     holds the focus, so a menu out there is a menu that can be seen and not
     pressed. Then it goes into the dialog instead, which has no overflow to be
     cut by and is the thing being used. */
  const [host, setHost] = useState(null);
  const saved = useCustomPalettes();

  const custom = isCustomPalette(value);
  const estate = !custom && (!value || value === ESTATE_PALETTE);
  const current = estate ? { label: "Estate", nodePalette: ESTATE_STRIP } : paletteOf(value);

  /* The editor's own state, so fiddling with the colour does not keep writing
     half-made palettes onto the page behind the menu. It opens on whatever is
     chosen, or on the accent. */
  const [draft, setDraft] = useState(() => ({
    base: custom ? value.base : "#f6400d",
    scheme: custom ? value.scheme || DEFAULT_SCHEME : DEFAULT_SCHEME,
    name: custom ? value.name || "" : "",
    /* null while the ten are whatever the colour and the scheme make of
       themselves; an array once they have been taken off a picture, greyed, or
       edited one at a time - at which point they are the palette and the
       scheme is only how they started */
    colors: custom && Array.isArray(value.colors) ? value.colors : null,
  }));

  /* what the draft actually makes, which is the one thing drawn below */
  const draftColors = draft.colors || buildPalette(draft.base, draft.scheme);

  /* Reading a picture takes a moment and can fail; both are worth saying. */
  const [reading, setReading] = useState("");
  const file = useRef(null);

  useEffect(() => {
    if (!open) return;
    setDraft(d => (isCustomPalette(value)
      ? {
        base: value.base,
        scheme: value.scheme || DEFAULT_SCHEME,
        name: value.name || "",
        colors: Array.isArray(value.colors) ? value.colors : null,
      }
      : d));
  }, [open, value]);

  /* The ten as they stand, whatever made them: what the greyscale button and
     the swatch pickers work on. */
  const takeColors = (colors, extra = {}) => setDraft(d => ({
    ...d,
    colors,
    /* the lightest leads, since that is what everything else reads as the
       accent of a palette */
    base: colors[0] || d.base,
    ...extra,
  }));

  const fromPicture = async (chosen) => {
    if (!chosen) return;
    setReading("reading…");
    try {
      const colors = await paletteFromImage(chosen, 10);
      takeColors(colors, { name: draft.name || chosen.name.replace(/\.[^.]+$/, "").slice(0, 40) });
      setReading("");
    } catch (e) {
      setReading(e?.message || "that picture could not be read");
    }
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    /* the nearest dialog, if this picker is in one; the body otherwise */
    setHost(wrapRef.current?.closest(".dialog-panel, .MuiDialog-container, .MuiPaper-root") || document.body);

    const place = () => {
      const trigger = wrapRef.current?.getBoundingClientRect();
      if (!trigger) return;
      const width = 320;
      /* Measured rather than assumed: the menu is as tall as the palettes
         someone has made, and a menu that runs off the bottom of the window is
         a menu whose last rows cannot be reached. It opens downwards where
         there is room and upwards where there is not. */
      const tall = menuRef.current?.offsetHeight || 420;
      const below = window.innerHeight - trigger.bottom - 12;
      const up = tall > below && trigger.top > below;
      setAt({
        top: Math.round(up
          ? Math.max(12, trigger.top - tall - 6)
          : Math.min(trigger.bottom + 6, Math.max(12, window.innerHeight - tall - 12))),
        left: Math.round(Math.min(Math.max(trigger.left - 8, 12), window.innerWidth - width - 12)),
      });
    };
    place();
    /* once more now that it has been drawn and its height is known */
    const again = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(again);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (next) => { onChange(next); setOpen(false); };

  return (
    <span className="card-palette" ref={wrapRef}>
      <button
        type="button"
        className={`card-palette__trigger${open ? " is-open" : ""}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={`Colour — ${current.label}`}
        title={`Colour — ${current.label}`}
      >
        <IconPalette />
        {/* what is chosen, as colour rather than as a word */}
        <span className="card-palette__dots" aria-hidden="true">
          {[0, 4, 8].map(i => (
            <i key={i} style={{ background: current.nodePalette[i % current.nodePalette.length] }} />
          ))}
        </span>
      </button>

      {/* Beside the picker rather than at the foot of its menu: "everywhere" is
          a thing you do to the colour you are looking at, and it was two
          clicks and a scroll away inside the thing that chooses one. */}
      {onApplyAll && (
        <button
          type="button"
          className="card-palette__all"
          onClick={() => { onApplyAll(value); setOpen(false); }}
          title="Use this colour on every card page and every dashboard"
        >
          Use everywhere
        </button>
      )}

      {open && at && host && createPortal(
        <div
          className="card-palette__menu"
          ref={menuRef}
          style={{ top: `${at.top}px`, left: `${at.left}px` }}
          role="dialog"
          aria-label="Colour"
        >
          <header className="card-palette__head">
            <IconPalette />
            <span className="card-palette__title">Colour</span>
            <span className="card-palette__scope">{scopeLabel}</span>
          </header>

          <div className="card-palette__list">
            {allowEstate && (
              <button
                type="button"
                className={`card-palette__option${estate ? " is-active" : ""}`}
                onClick={() => pick(ESTATE_PALETTE)}
              >
                <Swatches colors={ESTATE_STRIP} />
                <span className="card-palette__name">Estate</span>
              </button>
            )}

            {paletteIds().map(id => {
              const theme = paletteOf(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={`card-palette__option${!custom && value === id ? " is-active" : ""}`}
                  onClick={() => pick(id)}
                >
                  <Swatches colors={theme.nodePalette} />
                  <span className="card-palette__name">{theme.label}</span>
                </button>
              );
            })}
          </div>

          {saved.length > 0 && (
            <>
              <span className="card-palette__label">Yours</span>
              <div className="card-palette__list card-palette__list--own">
                {saved.map(p => {
                  const mine = custom && customId(value) === customId(p);
                  return (
                    <span key={customId(p)} className={`card-palette__own${mine ? " is-active" : ""}`}>
                      <button
                        type="button"
                        className="card-palette__option"
                        onClick={() => pick({ base: p.base, scheme: p.scheme, ...(p.name ? { name: p.name } : {}) })}
                      >
                        <Swatches colors={buildPalette(p.base, p.scheme)} />
                        <span className="card-palette__name">{p.name || "Custom"}</span>
                      </button>
                      <button
                        type="button"
                        className="card-palette__forget"
                        title="Forget this palette"
                        aria-label={`Forget ${p.name || "this palette"}`}
                        onClick={() => forgetCustomPalette(p)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </span>
                  );
                })}
              </div>
            </>
          )}

          <span className="card-palette__label">Mix your own</span>
          <div className="card-palette__custom">
            <div className="card-palette__controls">
              <input
                type="color"
                className="card-palette__color"
                value={/^#[0-9a-f]{6}$/i.test(draft.base) ? draft.base : "#f6400d"}
                aria-label="Base colour"
                /* picking a colour starts the ten again from it: the swatches
                   someone edited are not a thing to keep half of */
                onChange={(e) => setDraft(d => ({ ...d, base: e.target.value, colors: null }))}
              />
              <select
                className="card-palette__scheme"
                value={draft.scheme}
                aria-label="Colour scheme"
                onChange={(e) => setDraft(d => ({ ...d, scheme: e.target.value, colors: null }))}
              >
                {PALETTE_SCHEMES.map(sch => (
                  <option key={sch.id} value={sch.id}>{sch.label}</option>
                ))}
              </select>
              <input
                type="text"
                className="card-palette__input"
                value={draft.name}
                maxLength={40}
                placeholder="Name it"
                aria-label="Palette name"
                onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
              />
            </div>

            {/* Where else ten colours can come from. A picture is the fast way
                to a palette nobody has to mix - a screenshot, a photograph, a
                brand sheet - and grey is the one palette that is always worth
                being one press away. */}
            <div className="card-palette__sources">
              <input
                ref={file}
                type="file"
                accept="image/*"
                className="card-palette__file"
                onChange={(e) => { fromPicture(e.target.files?.[0]); e.target.value = ""; }}
              />
              <button
                type="button"
                className="card-palette__source"
                onClick={() => file.current?.click()}
                title="Take ten colours off a picture"
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M3 16l5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="9" cy="9.5" r="1.4" fill="currentColor" />
                </svg>
                From a picture
              </button>

              <button
                type="button"
                className="card-palette__source"
                onClick={() => takeColors(greyscale(draftColors))}
                title="The same ten with the colour taken out"
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M12 3.6v16.8a8.4 8.4 0 0 0 0-16.8Z" fill="currentColor" />
                </svg>
                Greyscale
              </button>

              {reading && <span className="card-palette__note">{reading}</span>}
            </div>

            {/* the ten it actually makes - and each of them its own, because a
                palette is nine tenths right more often than it is wrong */}
            <span className="card-palette__swatches card-palette__swatches--wide card-palette__swatches--edit">
              {draftColors.map((c, i) => (
                <label key={`${i}-${c}`} className="card-palette__swatch" title={`Swatch ${i + 1} — ${c}`}>
                  <span style={{ background: c }} />
                  <input
                    type="color"
                    value={/^#[0-9a-f]{6}$/i.test(c) ? c : "#808080"}
                    aria-label={`Swatch ${i + 1}`}
                    onChange={(e) => {
                      const next = [...draftColors];
                      next[i] = e.target.value;
                      takeColors(next);
                    }}
                  />
                </label>
              ))}
            </span>

            <div className="card-palette__mixfoot">
              <button
                type="button"
                className="card-palette__use"
                onClick={() => pick({
                  base: draft.base,
                  scheme: draft.scheme,
                  ...(draft.colors ? { colors: draft.colors } : {}),
                  ...(draft.name.trim() ? { name: draft.name.trim() } : {}),
                })}
              >
                Use this one
              </button>

              {draft.colors && (
                <button
                  type="button"
                  className="card-palette__back"
                  onClick={() => setDraft(d => ({ ...d, colors: null }))}
                  title="Back to the ten this colour and scheme make"
                >
                  Start over
                </button>
              )}
            </div>
          </div>

        </div>,
        host,
      )}
    </span>
  );
}
